<template>
  <div class="page-container">
    <el-card v-loading="pageLoading">
      <div slot="header" class="page-header">
        <span class="page-title">{{ isEdit ? '编辑优惠券' : '新增优惠券' }}</span>
        <div class="page-actions">
          <el-button size="small" @click="handleCancel">取消</el-button>
          <el-button type="primary" size="small" :loading="saving" @click="handleSave">
            {{ isEdit ? '更新' : '保存' }}
          </el-button>
        </div>
      </div>

      <el-form ref="couponForm" :model="form" :rules="rules" label-width="120px" class="coupon-form">

        <el-form-item label="优惠券名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入优惠券名称" class="w-md" />
        </el-form-item>

        <el-form-item label="减免金额" prop="amount">
          <el-input-number v-model="form.amount" :min="0.01" :precision="2" :step="10" />
          <span class="form-tip ml">元</span>
        </el-form-item>

        <el-form-item label="可用产品" prop="product_id">
          <div class="product-selector">
            <el-tag
              v-if="form.productName"
              closable
              size="medium"
              type="success"
              @close="clearProduct"
            >{{ form.productName }}</el-tag>
            <el-button size="mini" :type="form.product_id ? 'default' : 'primary'" @click="openProductDialog">
              {{ form.product_id ? '重新选择' : '选择产品' }}
            </el-button>
          </div>
          <div class="form-tip">必须选择一个产品（优惠券与产品绑定）</div>
        </el-form-item>

        <el-form-item label="有效期" prop="dateRange">
          <el-date-picker
            v-model="form.dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:420px"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button size="small" @click="handleCancel">取消</el-button>
          <el-button type="primary" size="small" :loading="saving" @click="handleSave">
            {{ isEdit ? '更新' : '保存' }}
          </el-button>
        </el-form-item>

      </el-form>
    </el-card>

    <!-- 产品选择弹窗 -->
    <el-dialog
      title="选择产品"
      :visible.sync="productDialog.visible"
      width="780px"
      @open="fetchProductList"
      @close="productDialog.keyword = ''"
    >
      <div class="dialog-search">
        <el-input
          v-model="productDialog.keyword"
          placeholder="搜索产品号/名称/略称"
          clearable
          style="width:240px"
          @keyup.enter.native="searchProducts"
        />
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchProducts">查询</el-button>
        <el-button size="mini" @click="productDialog.keyword = ''; fetchProductList()">重置</el-button>
      </div>

      <el-table
        :data="productDialog.list"
        v-loading="productDialog.loading"
        border
        size="small"
        highlight-current-row
        style="width:100%;margin-top:10px"
        @current-change="handleProductSelect"
      >
        <el-table-column width="46" align="center">
          <template slot-scope="{ row }">
            <el-radio
              :value="productDialog.selected && productDialog.selected.id"
              :label="row.id"
              @change="handleProductSelect(row)"
            >&nbsp;</el-radio>
          </template>
        </el-table-column>
        <el-table-column prop="id" label="ID" width="60" align="center" />
        <el-table-column label="缩略图" width="66" align="center">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.thumbnail"
              :src="row.thumbnail"
              fit="cover"
              style="width:40px;height:40px;border-radius:3px;display:block;margin:0 auto"
            />
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="product_no" label="产品号" width="120" />
        <el-table-column prop="name" label="产品名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="实收价" width="100" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥{{ Number(row.price).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="70" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
              {{ row.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page="productDialog.page"
        :page-size="productDialog.pageSize"
        :total="productDialog.total"
        layout="total, prev, pager, next"
        style="margin-top:10px;text-align:right"
        @current-change="val => { productDialog.page = val; fetchProductList() }"
      />

      <div slot="footer">
        <el-button size="small" @click="productDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :disabled="!productDialog.selected"
          @click="confirmProduct"
        >确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCouponDetail, createCoupon, updateCoupon } from '@/api/coupon'
import { getProductList } from '@/api/product'

const defaultForm = () => ({
  name: '',
  amount: 0,
  product_id: null,
  productName: '',
  dateRange: [],
  status: 1
})

export default {
  name: 'CouponEdit',
  data() {
    return {
      pageLoading: false,
      saving: false,
      form: defaultForm(),
      rules: {
        name:   [{ required: true, message: '优惠券名称不能为空', trigger: 'blur' }],
        amount: [{ required: true, type: 'number', min: 0.01, message: '请输入减免金额', trigger: 'change' }],
        product_id: [{
          required: true,
          validator: (rule, value, callback) => {
            if (!value) callback(new Error('请选择可用产品'))
            else callback()
          },
          trigger: 'change'
        }],
        dateRange: [{
          required: true,
          validator: (rule, value, callback) => {
            if (!value || !value[0] || !value[1]) callback(new Error('请选择有效期'))
            else callback()
          },
          trigger: 'change'
        }]
      },
      productDialog: {
        visible: false,
        loading: false,
        keyword: '',
        list: [],
        page: 1,
        pageSize: 10,
        total: 0,
        selected: null
      }
    }
  },
  computed: {
    couponId() {
      return this.$route.query.id ? Number(this.$route.query.id) : null
    },
    isEdit() {
      return !!this.couponId
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/coupon/edit') return
        if (to.query.id) {
          this.fetchDetail()
        } else {
          this.form = defaultForm()
        }
      }
    }
  },
  methods: {
    async fetchDetail() {
      this.pageLoading = true
      try {
        const res = await getCouponDetail({ id: this.couponId })
        const d = res.data
        this.form = {
          name:        d.name,
          amount:      d.amount,
          product_id:  d.product_id || null,
          productName: d.product ? d.product.name : '',
          dateRange:   d.started_at && d.expired_at ? [d.started_at, d.expired_at] : [],
          status:      d.status
        }
      } finally {
        this.pageLoading = false
      }
    },

    // ── 产品弹窗 ────────────────────────────────────────────
    openProductDialog() {
      this.productDialog.selected = null
      this.productDialog.visible = true
    },

    searchProducts() {
      this.productDialog.page = 1
      this.fetchProductList()
    },

    async fetchProductList() {
      this.productDialog.loading = true
      try {
        const res = await getProductList({
          keyword:  this.productDialog.keyword,
          page:     this.productDialog.page,
          pageSize: this.productDialog.pageSize
        })
        const d = res.data || {}
        this.productDialog.list  = d.data || []
        this.productDialog.total = d.total || 0
      } finally {
        this.productDialog.loading = false
      }
    },

    handleProductSelect(row) {
      if (row) this.productDialog.selected = row
    },

    confirmProduct() {
      if (!this.productDialog.selected) return
      this.form.product_id  = this.productDialog.selected.id
      this.form.productName = this.productDialog.selected.name
      this.$refs.couponForm && this.$refs.couponForm.validateField('product_id')
      this.productDialog.visible = false
    },

    clearProduct() {
      this.form.product_id  = null
      this.form.productName = ''
    },

    // ── 保存 ────────────────────────────────────────────────
    handleSave() {
      this.$refs.couponForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            name:       this.form.name,
            amount:     this.form.amount,
            product_id: this.form.product_id,
            started_at: this.form.dateRange[0],
            expired_at: this.form.dateRange[1],
            status:     this.form.status
          }
          if (this.isEdit) {
            await updateCoupon({ id: this.couponId, ...payload })
            this.$message.success('更新成功')
          } else {
            await createCoupon(payload)
            this.$message.success('创建成功')
          }
          this.$router.push('/coupon/index')
        } finally {
          this.saving = false
        }
      })
    },

    handleCancel() {
      this.$router.push('/coupon/index')
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .page-title { font-size: 15px; font-weight: 600; color: #303133; }
  .page-actions { display: flex; gap: 8px; }
}

.coupon-form {
  max-width: 760px;
  padding-top: 8px;
  .w-md { width: 340px; }
}

.product-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: block;
  &.ml { display: inline; margin-top: 0; margin-left: 8px; }
}

.dialog-search {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-text { color: #f56c6c; font-weight: 600; }
.text-muted { color: #c0c4cc; font-size: 12px; }
</style>
